/**
 * The week, as the settings form has to hold it.
 *
 * Pure and React-free, so the fiddly half of the working-hours tab - which day
 * is which index, what a stored minute count looks like in a field, what "copy
 * this day to the rest" means - is testable without rendering anything.
 *
 * The storage order is the server's: index 0 is Sunday, because the server reads
 * that array against a clock and `Date.getDay()` numbers Sunday zero. The screen
 * order is this country's. `WEEK_ORDER` is the entire conversion between them,
 * and it lives here so no component has to think about it twice.
 */
import type { BusinessDay } from "../../../../lib/calls";

/** Screen order: Monday first, Sunday last. Values are `getDay()` indexes. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * Granularity of the two time fields, in minutes.
 *
 * Quarter hours rather than a free-text clock: a phone line opens at 08:00,
 * 08:30 or 09:15, and a typed field would accept 08:7 and then have to explain
 * itself. The same step the chat widget's work-hours editor already offers, so
 * the two schedules in this product are set the same way.
 */
export const MINUTES_STEP = 15;

export const MINUTES_PER_DAY = 24 * 60;

/** A stored minute count as a clock face: 540 reads 09:00. */
export function clockLabel(minutes: number): string {
  const total = ((Math.trunc(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** ...and back, for what a select hands over. Anything unreadable stays put. */
export function minutesFromClock(value: string, fallback: number): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return fallback;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return Number.isFinite(minutes) && minutes >= 0 && minutes < MINUTES_PER_DAY ? minutes : fallback;
}

/** Every quarter hour of the day, as select options. */
export function timeOptions(): { value: string; label: string }[] {
  const count = MINUTES_PER_DAY / MINUTES_STEP;
  return Array.from({ length: count }, (_, i) => {
    const label = clockLabel(i * MINUTES_STEP);
    return { value: label, label };
  });
}

/**
 * The closing field's options: the same list, with midnight at the END rather
 * than the beginning.
 *
 * Minutes only run to 1439, so "open until the end of the day" has to be stored
 * as 00:00 - the start of the next one. Left where it falls in the list, that
 * option sits at the top labelled 00:00 and reads as a window that shuts before
 * it opens; moved to the bottom and labelled 24:00 it reads as what it is. The
 * VALUE is still 00:00, so nothing downstream has to know about the relabelling.
 */
export function closingOptions(): { value: string; label: string }[] {
  const [midnight, ...rest] = timeOptions();
  return midnight ? [...rest, { value: midnight.value, label: "24:00" }] : [];
}

/** The shipped week: Monday to Friday, nine to five. Mirrors `defaultDays()`. */
export function defaultWeek(): BusinessDay[] {
  return Array.from({ length: 7 }, (_, weekday) => ({
    open: weekday >= 1 && weekday <= 5,
    from: 9 * 60,
    to: 17 * 60,
  }));
}

/**
 * Seven entries, whatever came back.
 *
 * A settings row written by an older shape, or by hand, can be short - and the
 * entry this fills in is the one the editor would otherwise render as an empty
 * row with no switch in it.
 */
export function normaliseWeek(days: readonly BusinessDay[] | undefined): BusinessDay[] {
  const fallback = defaultWeek();
  return fallback.map((day, weekday) => {
    const stored = days?.[weekday];
    return stored ? { ...stored } : day;
  });
}

/** Open or close one weekday, leaving its times where they were. */
export function toggleDay(days: readonly BusinessDay[], weekday: number): BusinessDay[] {
  return days.map((day, index) => (index === weekday ? { ...day, open: !day.open } : { ...day }));
}

/** Move one edge of one weekday's window. */
export function setDayTime(
  days: readonly BusinessDay[],
  weekday: number,
  edge: "from" | "to",
  minutes: number,
): BusinessDay[] {
  return days.map((day, index) => (index === weekday ? { ...day, [edge]: minutes } : { ...day }));
}

/**
 * Give every OPEN day the same window as this one.
 *
 * Only the open ones, deliberately. The button exists because setting five
 * identical weekdays one field at a time is ten interactions to say one thing;
 * it does not exist to open Saturday, which is a decision rather than a chore.
 */
export function copyWindowToOpenDays(
  days: readonly BusinessDay[],
  weekday: number,
): BusinessDay[] {
  const source = days[weekday];
  if (!source) return days.map((day) => ({ ...day }));
  return days.map((day) => (day.open ? { ...day, from: source.from, to: source.to } : { ...day }));
}

/** The first open day in screen order, which is the one worth copying FROM. */
export function firstOpenDay(days: readonly BusinessDay[]): number | null {
  for (const weekday of WEEK_ORDER) {
    if (days[weekday]?.open) return weekday;
  }
  return null;
}

/**
 * A window with no length: the same minute at both ends.
 *
 * Reads as closed on the server, which is right, but silently - so the editor
 * has to be the one to say it, or a day that looks ticked is a day nobody is
 * ever put through on.
 */
export function isEmptyWindow(day: BusinessDay): boolean {
  return day.open && day.from === day.to;
}

/** Does this day's window run past midnight into the next one? */
export function isOvernight(day: BusinessDay): boolean {
  return day.open && day.to !== 0 && day.to < day.from;
}

/**
 * How a form reads a window whose end is before its start: a night shift that
 * runs into the next day (the phone line), or a closed day (AI calling, whose
 * form says so in its own note).
 */
export type OvernightMeaning = "shift" | "closed";

/** Should the week editor explain a window past midnight as a night shift? Not where it means a closed day. */
export function showsNightShiftNote(days: readonly BusinessDay[], overnight: OvernightMeaning): boolean {
  return overnight === "shift" && days.some(isOvernight);
}
